import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Pencil } from 'lucide-react';
import { ordersAPI } from '@/lib/api';
import { formatCurrency, formatDate, getStatusLabel, getStatusClass } from '@/lib/utils';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * Standalone read-only dialog to preview an order from anywhere.
 * Mirrors the visual conventions of ManualReceptionDetailDialog.
 *
 * Props:
 *   orderId: string | null
 *   onClose: () => void
 */
export default function OrderDetailDialog({ orderId, onClose }) {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (!orderId) { setOrder(null); return; }
        setLoading(true);
        ordersAPI.getOrderById(orderId)
            .then(res => setOrder(res.data))
            .catch(() => setOrder(null))
            .finally(() => setLoading(false));
    }, [orderId]);

    const goToEdit = () => {
        if (!orderId) return;
        navigate(`/orders?tab=orders&openOrder=${orderId}`);
        onClose();
    };

    return (
        <Dialog open={!!orderId} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent
                className="sm:max-w-6xl max-h-[90vh] flex flex-col gap-2"
                data-testid="order-standalone-dialog"
            >
                {loading ? (
                    <div className="py-12 flex items-center justify-center text-slate-400 text-sm">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement de la commande…
                    </div>
                ) : order ? (
                    <>
                        <div className="flex-shrink-0 flex items-center gap-4 pb-2 border-b flex-wrap">
                            <h2 className="text-lg font-semibold whitespace-nowrap">
                                {order.order_number}
                            </h2>
                            <div className="flex items-center gap-4 text-xs flex-1 min-w-0 flex-wrap">
                                <span className="text-slate-500">{order.supplier_name}</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-500">{order.site_name}</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-slate-500">{formatDate(order.received_at || order.created_at)}</span>
                                {order.document_number && (
                                    <>
                                        <span className="text-slate-400">|</span>
                                        <span className="text-slate-500">{order.document_number}</span>
                                    </>
                                )}
                                {order.status && (
                                    <>
                                        <span className="text-slate-400">|</span>
                                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${getStatusClass(order.status)}`}>
                                            {getStatusLabel(order.status)}
                                        </span>
                                    </>
                                )}
                                <span className="text-slate-400">|</span>
                                <span className="font-bold text-emerald-600">{formatCurrency(order.total_amount)}</span>
                            </div>
                        </div>

                        <div className="flex-1 overflow-auto min-h-0 py-2">
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="text-left p-3">Produit</th>
                                            <th className="text-right p-3">Prix unit.</th>
                                            <th className="text-right p-3">Qté commandée</th>
                                            <th className="text-right p-3">Qté reçue</th>
                                            <th className="text-right p-3">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(order.items_details || []).map((item, index) => (
                                            <tr key={index} className="border-t">
                                                <td className="p-3">
                                                    {item.supplier_ref && <span className="font-mono text-xs text-slate-400 mr-2">{item.supplier_ref}</span>}
                                                    <span>{item.product_name || 'Produit'}</span>
                                                </td>
                                                <td className="p-3 text-right">{formatCurrency(item.price_per_unit || item.unit_price || 0)}</td>
                                                <td className="p-3 text-right">{item.quantity} {item.unit || ''}</td>
                                                <td className="p-3 text-right">{item.received_quantity ?? '—'} {item.unit || ''}</td>
                                                <td className="p-3 text-right font-medium">{formatCurrency((item.price_per_unit || 0) * (item.received_quantity ?? item.quantity ?? 0))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-emerald-50">
                                        <tr>
                                            <td colSpan="4" className="p-3 text-right font-semibold">Total :</td>
                                            <td className="p-3 text-right font-bold text-emerald-700">{formatCurrency(order.total_amount)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" onClick={onClose} data-testid="order-standalone-close-btn">
                                Fermer
                            </Button>
                            <Button
                                onClick={goToEdit}
                                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                                data-testid="order-standalone-edit-btn"
                            >
                                <Pencil className="w-4 h-4 mr-2" />
                                Ouvrir dans Commandes
                            </Button>
                        </div>
                    </>
                ) : (
                    <div className="py-12 text-center text-slate-400 text-sm">
                        Commande introuvable.
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
